import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner, Row, Col, InputGroup } from 'react-bootstrap';

const TwoFactorSetup = ({ show, onHide, onSetupComplete }) => {
  const [step, setStep] = useState(1); // 1: Setup, 2: Verify, 3: Backup codes
  const [qrCodeData, setQrCodeData] = useState('');
  const [secret, setSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [manualEntry, setManualEntry] = useState(false);

  // Initialiser le setup du 2FA
  const initializeSetup = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch('/api/2fa/setup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ password })
      });
      
      const data = await response.json();
      console.log('🔧 DEBUG TwoFactorSetup - Response data:', data);
      
      if (response.ok) {
        setQrCodeData(data.qrCode); // Utiliser 'qrCode' au lieu de 'qrCodeUrl'
        setSecret(data.manualEntryKey); // Utiliser la clé formatée pour l'affichage
        console.log('🔧 DEBUG TwoFactorSetup - QR Code set:', data.qrCode ? 'Yes' : 'No');
        setStep(2);
      } else {
        setError(data.message || 'Erreur lors de l\'initialisation du 2FA');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  // Vérifier le code et activer le 2FA
  const verifyAndActivate = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Veuillez entrer un code à 6 chiffres');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch('/api/2fa/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ 
          token: verificationCode,
          password 
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setBackupCodes(data.backupCodes);
        setStep(3);
      } else {
        setError(data.message || 'Code de vérification invalide');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  // Finaliser le setup
  const completeSetup = () => {
    onSetupComplete();
    onHide();
    // Réinitialiser l'état pour la prochaine fois
    setStep(1);
    setPassword('');
    setVerificationCode('');
    setError('');
  };

  // Réinitialiser quand le modal se ferme
  const handleClose = () => {
    setStep(1);
    setPassword('');
    setVerificationCode('');
    setError('');
    setQrCodeData('');
    setSecret('');
    setBackupCodes([]);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fas fa-shield-alt me-2"></i>
          Configuration de l'authentification à deux facteurs
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {error && (
          <Alert variant="danger" className="mb-3">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </Alert>
        )}

        {/* Étape 1: Vérification du mot de passe */}
        {step === 1 && (
          <div>
            <Alert variant="info">
              <i className="fas fa-info-circle me-2"></i>
              Pour configurer l'authentification à deux facteurs, veuillez d'abord confirmer votre mot de passe.
            </Alert>
            
            <Form.Group className="mb-3">
              <Form.Label>
                <i className="fas fa-lock me-2"></i>
                Mot de passe actuel
              </Form.Label>
              <Form.Control
                type="password"
                placeholder="Entrez votre mot de passe"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && initializeSetup()}
              />
            </Form.Group>
          </div>
        )}

        {/* Étape 2: Scan du QR Code et vérification */}
        {step === 2 && (
          <div>
            <Alert variant="success">
              <i className="fas fa-check-circle me-2"></i>
              Votre secret 2FA a été généré ! Suivez les étapes ci-dessous.
            </Alert>

            <Row>
              <Col md={6}>
                <h5><i className="fas fa-qrcode me-2"></i>1. Scanner le QR Code</h5>
                <p className="text-muted small">
                  Utilisez une application comme Google Authenticator, Authy, ou Microsoft Authenticator
                </p>
                
                {qrCodeData && (
                  <div className="text-center p-3 border rounded">
                    <img 
                      src={qrCodeData} 
                      alt="QR Code 2FA" 
                      style={{ width: '200px', height: '200px' }}
                      className="img-fluid"
                    />
                  </div>
                )}
                
                <div className="mt-3">
                  <Button 
                    variant="outline-secondary" 
                    size="sm"
                    onClick={() => setManualEntry(!manualEntry)}
                  >
                    <i className="fas fa-keyboard me-2"></i>
                    {manualEntry ? 'Masquer' : 'Saisie manuelle'}
                  </Button>
                </div>
                
                {manualEntry && (
                  <Alert variant="light" className="mt-2">
                    <small>
                      <strong>Clé secrète :</strong><br />
                      <code>{secret}</code>
                    </small>
                  </Alert>
                )}
              </Col>
              
              <Col md={6}>
                <h5><i className="fas fa-mobile-alt me-2"></i>2. Vérifier le code</h5>
                <p className="text-muted small">
                  Entrez le code à 6 chiffres généré par votre application
                </p>
                
                <Form.Group>
                  <Form.Label>Code de vérification</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder="000000"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      onKeyPress={(e) => e.key === 'Enter' && verifyAndActivate()}
                      className="text-center"
                      style={{ fontSize: '1.2em', letterSpacing: '0.1em' }}
                    />
                    <Button variant="primary" onClick={verifyAndActivate} disabled={loading}>
                      {loading ? <Spinner size="sm" /> : <i className="fas fa-check"></i>}
                    </Button>
                  </InputGroup>
                  <Form.Text className="text-muted">
                    Le code change toutes les 30 secondes
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </div>
        )}

        {/* Étape 3: Codes de récupération */}
        {step === 3 && (
          <div>
            <Alert variant="warning">
              <i className="fas fa-exclamation-triangle me-2"></i>
              <strong>Important !</strong> Sauvegardez ces codes de récupération dans un endroit sûr.
              Vous pourrez les utiliser pour accéder à votre compte si vous perdez votre appareil.
            </Alert>

            <h5><i className="fas fa-key me-2"></i>Codes de récupération</h5>
            <p className="text-muted">
              Chaque code ne peut être utilisé qu'une seule fois. Il vous reste {backupCodes.length} codes.
            </p>

            <div className="bg-light p-3 rounded border">
              <Row>
                {backupCodes.map((code, index) => (
                  <Col md={6} key={index} className="mb-2">
                    <code className="d-block p-2 bg-white border rounded text-center">
                      {code}
                    </code>
                  </Col>
                ))}
              </Row>
            </div>

            <div className="mt-3 d-flex gap-2">
              <Button
                variant="outline-primary"
                onClick={() => {
                  const codesText = backupCodes.join('\n');
                  navigator.clipboard.writeText(codesText);
                }}
              >
                <i className="fas fa-copy me-2"></i>
                Copier les codes
              </Button>
              
              <Button
                variant="outline-primary"
                onClick={() => {
                  const dataStr = `data:text/plain;charset=utf-8,${encodeURIComponent(backupCodes.join('\n'))}`;
                  const downloadAnchorNode = document.createElement('a');
                  downloadAnchorNode.setAttribute('href', dataStr);
                  downloadAnchorNode.setAttribute('download', 'backup-codes-2fa.txt');
                  document.body.appendChild(downloadAnchorNode);
                  downloadAnchorNode.click();
                  downloadAnchorNode.remove();
                }}
              >
                <i className="fas fa-download me-2"></i>
                Télécharger
              </Button>
            </div>

            <Alert variant="success" className="mt-3">
              <i className="fas fa-shield-alt me-2"></i>
              L'authentification à deux facteurs est maintenant activée ! Votre compte est plus sécurisé.
            </Alert>
          </div>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Annuler
        </Button>
        
        {step === 1 && (
          <Button 
            variant="primary" 
            onClick={initializeSetup}
            disabled={!password || loading}
          >
            {loading ? <Spinner size="sm" /> : 'Continuer'}
          </Button>
        )}
        
        {step === 2 && (
          <Button 
            variant="primary" 
            onClick={verifyAndActivate}
            disabled={!verificationCode || verificationCode.length !== 6 || loading}
          >
            {loading ? <Spinner size="sm" /> : 'Vérifier et activer'}
          </Button>
        )}
        
        {step === 3 && (
          <Button variant="success" onClick={completeSetup}>
            <i className="fas fa-check me-2"></i>
            Terminer
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default TwoFactorSetup;
